import { useEffect, useRef } from 'react';
import { flex } from '@fabric-ui/theme';
import { SxProps, Theme } from '@mui/material';
import { CellContext, ColumnSizingState, Table } from '@tanstack/react-table';
import { generateInnerDefaultFormatter } from '../Table';
import { DataRecord } from './table.types';

export const getAccessorKeyFn = key => row => row[key];

export const defaultColumnCell = (info: CellContext<any, any>) =>
  generateInnerDefaultFormatter({
    value: info.getValue() || info.row.original[info.column.id],
    type: info.column.columnDef.meta?.type,
    shouldDisplayNull: info.table.options.meta?.shouldDisplayNull,
  });

export const getSizeKey = (id: string, prefix: 'header' | 'col') => `--${prefix}-${id.replaceAll('.', '-')}-size`;

export const inlineActionsStyle: SxProps<Theme> = {
  position: 'sticky',
  right: 0,
  borderBottom: 'inherit',
  '> .inline-actions-cell': {
    position: 'absolute',
    padding: '0 24px 0 32px',
    top: '2px',
    right: '2px',
    height: 'calc(100% - 4px)',
    ...flex.justifyCenter,
    pt: 'inherit',
    pb: 'inherit',
    backgroundImage: (theme: Theme) => `linear-gradient(to right, transparent, ${theme.palette.colors.neutrals[200]} 20%)`,
    opacity: 0,
    transition: (theme: Theme) =>
      theme.transitions.create(['opacity'], {
        duration: theme.transitions.duration.short,
      }),
  },
  '.MuiTableRow-hover:hover &:not(:empty) > .inline-actions-cell, &:has(.Mui-focusVisible) > .inline-actions-cell': {
    opacity: 1,
  },
  '.MuiTableRow-hover.Mui-selected & > .inline-actions-cell': {
    backgroundImage: theme => `linear-gradient(to right, transparent, ${theme.palette.colors.primary[150]} 20%)`,
  },
  '.MuiButton-root': {
    minWidth: '40px',
    ...flex.center,
  },
};
export const useColumnResizeEnd = <TData extends DataRecord>(table: Table<TData>, onDragEnd: (newSizing: ColumnSizingState) => void) => {
  const tableRef = useRef(table);
  tableRef.current = table;

  const onDragEndRef = useRef(onDragEnd);
  onDragEndRef.current = onDragEnd;

  const isResizingRef = useRef<string | false>(false);
  isResizingRef.current = table.getState().columnSizingInfo.isResizingColumn;

  useEffect(() => {
    const handlePointerUp = () => {
      if (isResizingRef.current) {
        onDragEndRef.current(tableRef.current.getState().columnSizing);
      }
    };

    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, []);
};
